import { TCoinInputDto } from '../stateManagement'

const getClampUptoRangeAmount = (
    amount: number,
    shouldGetFloor: boolean,
    marginPercentage: number = 1,
) => {
    const roundPrecision = 1000000000
    const adjustedAmount = amount * marginPercentage * roundPrecision

    return shouldGetFloor
        ? Math.floor(adjustedAmount) / roundPrecision
        : Math.ceil(adjustedAmount) / roundPrecision
}

const validateAmount = ({
    coinCode,
    amount,
    minAmount,
    maxAmount,
}: {
    coinCode: string
    amount: number
    minAmount: number
    maxAmount: number
}): { validatedAmount: number; errorMessage: string } => {
    // Use small buffer for floating point comparison
    const buffer = 0.00000001
    const isWithinRange = amount >= minAmount - buffer && amount <= maxAmount + buffer

    // If amount is within valid range (accounting for floating point precision), return as-is
    if (isWithinRange) {
        return {
            validatedAmount: amount,
            errorMessage: '',
        }
    }

    if (amount < minAmount) {
        return {
            validatedAmount: getClampUptoRangeAmount(minAmount, false, 1.01),
            errorMessage: `${coinCode} amount was lower then minimum allowed limit.`,
        }
    }

    if (amount > maxAmount) {
        return {
            validatedAmount: getClampUptoRangeAmount(maxAmount, true, 0.99),
            errorMessage: `${coinCode} amount was higher then maximum allowed limit.`,
        }
    }

    return {
        validatedAmount: amount,
        errorMessage: '',
    }
}

const checkIsAmountInLimit = (
    coinInputDto: TCoinInputDto,
): { validatedCoinInputDto: TCoinInputDto; errorMessage: string } => {
    const { code, amount, minAmount, maxAmount } = coinInputDto

    const { validatedAmount, errorMessage } = validateAmount({
        coinCode: code,
        amount,
        minAmount,
        maxAmount,
    })

    return { validatedCoinInputDto: { ...coinInputDto, amount: validatedAmount }, errorMessage }
}

const auditPairInputDtos = ({
    fromCoinInputDto,
    toCoinInputDto,
    isClampToRangeEnabled = true,
}: {
    fromCoinInputDto: TCoinInputDto
    toCoinInputDto: TCoinInputDto
    isClampToRangeEnabled?: boolean
}): { fixedFromInputDto: TCoinInputDto; fixedToInputDto: TCoinInputDto; errorMessage: string } => {
    const { validatedCoinInputDto: fixedFromInputDto, errorMessage: errorMessageFrom } =
        checkIsAmountInLimit(fromCoinInputDto)

    const { validatedCoinInputDto: fixedToInputDto, errorMessage: errorMessageTo } =
        checkIsAmountInLimit(toCoinInputDto)

    const fromCoinInputDtoReturn = isClampToRangeEnabled
        ? fixedFromInputDto
        : { ...fromCoinInputDto }
    const toCoinInputDtoReturn = isClampToRangeEnabled ? fixedToInputDto : { ...toCoinInputDto }

    // if one of amount not in range - backend do not return amounts, need to calculate and convert manually
    const exchangeRate: number = fromCoinInputDto.maxAmount / toCoinInputDto.maxAmount
    // convert by exchange rate affected coin input to match value of targeted coin input
    if (errorMessageFrom) {
        toCoinInputDtoReturn.amount = fromCoinInputDtoReturn.amount / exchangeRate
    }
    if (errorMessageTo) {
        fromCoinInputDtoReturn.amount = toCoinInputDtoReturn.amount * exchangeRate
    }

    return {
        fixedFromInputDto: fromCoinInputDtoReturn,
        fixedToInputDto: toCoinInputDtoReturn,
        errorMessage: errorMessageFrom || errorMessageTo,
    }
}

export { getClampUptoRangeAmount, checkIsAmountInLimit, validateAmount, auditPairInputDtos }
